/**
 * PronunciationBreakdown — per-word and per-phoneme pronunciation feedback.
 *
 * Renders inside {@link FeedbackCard}'s pronunciation section. Shows overall
 * accuracy / fluency / prosody, then each word colored by score, and — when
 * Azure returned phoneme LABELS — the per-phoneme scores with a "→ x" hint on
 * low sounds (the alternative Azure most likely heard instead).
 *
 * Phoneme labels are en-US only: en-GB/es/fr/de come back with EMPTY phoneme
 * names, and `free`/word-granularity turns carry no phonemes at all. The view is
 * data-driven — it only expands a word's phonemes when non-empty labels are
 * present, otherwise it falls back to word-level chips. No locale plumbing.
 *
 * Display-only (no callbacks) so it stays cheap inside the chat FlatList item.
 */
import React from 'react';
import { Box, VStack, HStack, Text } from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';
import type {
  PronunciationResult,
  PronunciationWord,
  PronunciationPhonemeData,
} from '../../types/conversation';
import { FS_XS, FS_SM, FS_MD, FS_LG } from '../../theme';
import { useDisplayTheme } from '../../hooks/useDisplayTheme';
import { getScoreStyleColor } from './pronunciationColors';

/** At/below this phoneme score, surface the "heard instead" hint. */
const LOW_PHONEME_SCORE = 60;
/** Above this many words, only expand phonemes for the weak ones (keeps long results compact). */
const MANY_WORDS = 6;
/** In long results, only show phonemes for words below this score. */
const WEAK_WORD_SCORE = 80;

function hasLabel(p: PronunciationPhonemeData): boolean {
  return typeof p.phoneme === 'string' && p.phoneme.trim().length > 0;
}

/** The alternative Azure most likely heard, when it differs from the target phoneme. */
function heardInstead(p: PronunciationPhonemeData): string | undefined {
  const top = p.nbestPhonemes?.[0];
  return top && top.phoneme !== p.phoneme ? top.phoneme : undefined;
}

const ScoreCell = React.memo(function ScoreCell({ label, score }: { label: string; score: number }) {
  const { theme, fontScale, ffs } = useDisplayTheme();
  return (
    <Box style={{ backgroundColor: theme.inputBg }} px="$3" py="$2" rounded="$md" alignItems="center" justifyContent="center">
      <Text allowFontScaling={false} style={{ fontSize: FS_XS * fontScale, color: theme.textSecondary, fontWeight: 'bold', textTransform: 'uppercase', ...ffs }}>
        {label}
      </Text>
      <Text allowFontScaling={false} style={{ fontSize: FS_LG * fontScale, fontWeight: '800', color: getScoreStyleColor(score, theme), ...ffs }}>
        {Math.round(score)}%
      </Text>
    </Box>
  );
});

const PhonemeChip = React.memo(function PhonemeChip({ phoneme }: { phoneme: PronunciationPhonemeData }) {
  const { theme, fontScale, ffs } = useDisplayTheme();
  const color = getScoreStyleColor(phoneme.accuracyScore, theme);
  const heard = phoneme.accuracyScore < LOW_PHONEME_SCORE ? heardInstead(phoneme) : undefined;
  return (
    <Box style={{ backgroundColor: theme.card, borderColor: color }} borderWidth={1} px="$2" py="$1" rounded="$md" mr="$1" mb="$1" alignItems="center">
      <HStack space="xs" alignItems="baseline">
        <Text allowFontScaling={false} style={{ fontSize: FS_SM * fontScale, fontWeight: '700', color, ...ffs }}>
          {phoneme.phoneme}
        </Text>
        <Text allowFontScaling={false} style={{ fontSize: FS_XS * fontScale, color: theme.textSecondary, ...ffs }}>
          {Math.round(phoneme.accuracyScore)}
        </Text>
      </HStack>
      {heard !== undefined && (
        <Text allowFontScaling={false} style={{ fontSize: FS_XS * fontScale, color: theme.error, fontStyle: 'italic', ...ffs }}>
          ≈ {heard}
        </Text>
      )}
    </Box>
  );
});

const WordBlock = React.memo(function WordBlock({
  word,
  index,
  showPhonemes,
}: {
  word: PronunciationWord;
  index: number;
  showPhonemes: boolean;
}) {
  const { theme, fontScale, ffs } = useDisplayTheme();
  const color = getScoreStyleColor(word.accuracyScore, theme);
  const isError = word.errorType !== undefined && word.errorType !== 'None';
  const phonemes = showPhonemes ? (word.phonemes ?? []).filter(hasLabel) : [];

  // Screen readers get the word + its scores in one read; the chips themselves
  // are decorative (numbers + IPA symbols) so we don't double-announce them.
  const a11yLabel = [
    `${word.word} ${Math.round(word.accuracyScore)}%`,
    ...phonemes.map((p) => `${p.phoneme} ${Math.round(p.accuracyScore)}`),
  ].join(', ');

  return (
    <Box
      accessible
      accessibilityLabel={a11yLabel}
      testID={`chat-pronunciation-word-${index}`}
      style={{ backgroundColor: theme.inputBg, borderColor: theme.border }}
      borderWidth={1}
      p="$2"
      rounded="$lg"
      mr="$2"
      mb="$2"
    >
      <HStack space="xs" alignItems="center">
        <Text allowFontScaling={false} style={{ fontSize: FS_MD * fontScale, fontWeight: '700', color, ...ffs }}>
          {word.word}
        </Text>
        <Text allowFontScaling={false} style={{ fontSize: FS_XS * fontScale, color: theme.textSecondary, ...ffs }}>
          {Math.round(word.accuracyScore)}%
        </Text>
        {isError && (
          <Text allowFontScaling={false} accessible={false} importantForAccessibility="no" style={{ fontSize: FS_XS * fontScale }}>
            ⚠️
          </Text>
        )}
      </HStack>
      {phonemes.length > 0 && (
        <HStack space="xs" flexWrap="wrap" mt="$1">
          {phonemes.map((p, i) => (
            <PhonemeChip key={i} phoneme={p} />
          ))}
        </HStack>
      )}
    </Box>
  );
});

export const PronunciationBreakdown = React.memo(function PronunciationBreakdown({
  result,
}: {
  result: PronunciationResult;
}) {
  const { t } = useTranslation();
  const words = result.words ?? [];
  const manyWords = words.length > MANY_WORDS;

  return (
    <VStack space="sm">
      {/* Overall scores */}
      <HStack space="md" flexWrap="wrap">
        <ScoreCell label={t('chat.analysis.accuracyLabel')} score={result.accuracyScore} />
        <ScoreCell label={t('chat.analysis.fluencyLabel')} score={result.fluencyScore} />
        {result.prosodyScore != null && (
          <ScoreCell label={t('chat.analysis.prosodyLabel')} score={result.prosodyScore} />
        )}
      </HStack>

      {/* Word (+ phoneme) breakdown */}
      {words.length > 0 && (
        <HStack space="xs" flexWrap="wrap">
          {words.map((w, i) => (
            <WordBlock
              key={i}
              index={i}
              word={w}
              showPhonemes={!manyWords || w.accuracyScore < WEAK_WORD_SCORE}
            />
          ))}
        </HStack>
      )}
    </VStack>
  );
});
